<script lang="ts">
    import "../app.css";
    import { onMount } from "svelte";
    import { page } from "$app/stores";
    import { browser } from "$app/environment";
    import { afterNavigate } from "$app/navigation";

    import "$lib/polyfills";
    import env from "$lib/env";
    import locale from "$lib/i18n/locale";
    import settings from "$lib/state/settings";

    import { t } from "$lib/i18n/translations";

    import { device, app } from "$lib/device";
    import { getServerInfo } from "$lib/api/server-info";
    import currentTheme, { statusBarColors } from "$lib/state/theme";

    import MediaDialog from "$components/dialog/MediaDialog.svelte";
    import { getTypeFromPath, typeList } from "$lib/util";

    $: preloadAssets = false;
    $: plausibleLoaded = false;

    let titleStore = $t('general.main.title');
    let descStore = $t('general.main.description');

    // 根据路径和参数设置标题
    $: {
        const pathname = $page.url.pathname;
        const type = getTypeFromPath(pathname)
        if(type){
            titleStore = $t('general.embed.title',{value:$t(`tabs.${type}`)});
            descStore = $t(`desc.${type}`);
        } else {
            titleStore = $t('general.embed.title',{value:$t('general.descriptionAll')});
            descStore = $t('general.embed.description',{value:$t('general.descriptionAll')});
        }
    }

   

    afterNavigate(async () => {
        const to_focus: HTMLElement | null =
            document.querySelector("[data-first-focus]");
        to_focus?.focus();
        await getServerInfo();
    });

    onMount(() => {
        preloadAssets = true;
    });
</script>

<svelte:head>
    <meta charset="utf-8">
    <title>{titleStore}</title>
    <meta name="description" content={descStore} />
    <meta property="og:title" content={titleStore} />
    <meta property="og:description" content={descStore} />
    <meta name="keywords" content={$t("general.main.keywords")} />
    <meta name="og:type" content="website">
    <meta property="og:site_name" content={$t("general.cobalt")} />
    <meta name="twitter:card" content="summary_large_image">
    <meta property="twitter:title" content={titleStore} />
    <meta property="twitter:description" content={descStore} />
     <meta name="viewport" content="viewport-fit=cover, width=device-width, height=device-height, initial-scale=1, maximum-scale=1">
    <meta name="application-name" content={$t("general.cobalt")}>
    <meta name="apple-mobile-web-app-title" content={$t("general.cobalt")}>
    <meta name="mobile-web-app-capable" content="yes">
    <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
    <!-- 移动端优化描述（可选，用于移动搜索） -->
    <meta name="mobile-web-app-capable" content="yes" />
    <meta name="apple-mobile-web-app-title" content={$t("general.cobalt")} />
      <!-- 规范链接（避免重复内容） -->
    <link rel="canonical" href="https://{env.HOST}/{$locale}" />
    <!-- 搜索引擎指令 -->
    <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1">

    <!-- 语言声明 -->
    <meta name="content-language" content="{$locale}">
    <link rel="alternate" hreflang="{$locale}" href="https://{env.HOST}/{$locale}">
     <!-- 结构化数据（Schema.org） -->
    <script type="application/ld+json">
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Instagram Video Downloader",
      "applicationCategory": "MultimediaApplication",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "15420"
      },
      "operatingSystem": "Web, iOS, Android",
      "description": "Free Instagram Video Downloader - Download Instagram videos, Reels, Stories & photos in HD quality online. No login required, fast & easy to use."
    }
    </script>
    <script type="application/ld+json">
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
            "@type": "Question",
            "name": "How To Download Instagram Online",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "InstaTube works on most devices (ie Windows, Mac, iPad, iPhone, Android, Tablet). Check the following guide to download video from Instagram online."
            }
        },
        ]
    }
    </script>

    {#if env.HOST}
        <meta
            property="og:url"
            content="https://{env.HOST}{$page.url.pathname}"
        />
        <meta
            property="og:image"
            content="https://{env.HOST}/images/logo-card.jpg"
        />
    	<meta property="twitter:domain" content="{env.HOST}"/>
    	<meta
            property="twitter:url"
            content="https://{env.HOST}{$page.url.pathname}"
        />
        <meta
            property="twitter:image:src"
            content="https://{env.HOST}/images/logo-card.jpg"
        />
    {/if}

    {#if device.is.mobile}
        <meta
            name="theme-color"
            content={statusBarColors.mobile[$currentTheme]}
        />
    {:else}
        <meta
            name="theme-color"
            content={statusBarColors.desktop[$currentTheme]}
        />
    {/if}

    {#if plausibleLoaded || (browser && env.PLAUSIBLE_ENABLED && !$settings.privacy.disableAnalytics)}
        <script
            defer
            data-domain={env.HOST}
            on:load={() => {
                plausibleLoaded = true;
            }}
            src="https://{env.PLAUSIBLE_HOST}/js/script.js"
        ></script>
    {/if}
    {#if env.WEB_SHOW_ADSENCE}
        <meta name="google-adsense-account" content="ca-pub-{env.WEB_ADSENCE_ID}"> 
        <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-{env.WEB_ADSENCE_ID}"
        crossorigin="anonymous"></script>
    {/if}
    
    {#if env.NODE_ENV === 'prod'}

        <script async src="https://www.googletagmanager.com/gtag/js?id=G-K08RZFNJMK"></script>
        <script>
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-K08RZFNJMK');
        </script>
    {/if} 
</svelte:head>

<div
    style="display: contents"
    data-theme={browser ? $currentTheme : undefined}
    lang={$locale}
>
    <!-- {#if preloadAssets}
        <div id="preload" aria-hidden="true">??</div>
    {/if} -->
    <!-- <div
        id="cobalt"
        class:loaded={browser}
        data-chrome={device.browser.chrome}
        data-iphone={device.is.iPhone}
        data-mobile={device.is.mobile}
        data-reduce-motion={reduceMotion}
        data-reduce-transparency={reduceTransparency}
    >
        {#if device.is.iPhone && app.is.installed}
            <NotchSticker />
        {/if}
        <Sidebar />
        <div id="content">
            {#if ($turnstileEnabled && $page.url.pathname === "/") || $turnstileCreated}
                <Turnstile />
            {/if} -->
            <slot></slot>
        <!-- </div> -->
    <!-- </div> -->
     <MediaDialog />
</div>

<style>
  
</style>
